import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { computeStoplicht, stoplichtBg, stoplichtTextColor, formatScheduledFor, formatDaysSilence } from '../../lib/journeyHelpers';
import type { JourneyOverviewRow, StoplichtKleur, UserJourneyStatus } from '../../types/klanttraject';
import { Search, Settings as SettingsIcon, Loader2, Inbox, List } from 'lucide-react';
import { TabNavigation } from '../../components/admin/TabNavigation';

type FilterKey = 'all' | 'green' | 'yellow' | 'red' | 'completed';

interface PendingTask {
  ujp_id: string;
  user_id: string;
  step_key: string;
  step_title: string;
  step_type: string;
  company_name: string | null;
  email: string;
  updated_at: string;
}

export function AdminKlanttraject() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<JourneyOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [tasks, setTasks] = useState<PendingTask[] | null>(null);
  const [tabOverride, setTabOverride] = useState<'open' | 'overzicht' | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: users } = await supabase
        .from('users')
        .select('id, email, company_name, journey_status, journey_last_activity_at')
        .neq('journey_status', 'inactive')
        .order('journey_last_activity_at', { ascending: false, nullsFirst: false });

      if (cancelled) return;
      if (!users || users.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const userIds = users.map((u: any) => u.id);
      const [progress, steps] = await Promise.all([
        supabase.from('user_journey_progress').select('user_id, step_key, status, scheduled_for').in('user_id', userIds),
        supabase.from('journey_steps').select('step_key, step_order, title').eq('is_active', true).order('step_order'),
      ]);
      if (cancelled) return;

      const stepByKey = new Map((steps.data ?? []).map((s: any) => [s.step_key, s]));
      const totalSteps = (steps.data ?? []).length;

      const built: JourneyOverviewRow[] = users.map((u: any) => {
        const userProg = (progress.data ?? []).filter((p: any) => p.user_id === u.id);
        const open = userProg
          .filter((p: any) => p.status !== 'completed' && p.status !== 'skipped')
          .sort((a: any, b: any) => ((stepByKey.get(a.step_key) as any)?.step_order ?? 999) - ((stepByKey.get(b.step_key) as any)?.step_order ?? 999));
        const current = open[0];
        const currentStep = current ? (stepByKey.get(current.step_key) as any) : null;
        const hasFailedOrManual = userProg.some((p: any) => p.status === 'failed' || p.status === 'pending_manual');
        return {
          user_id: u.id,
          email: u.email ?? '',
          company_name: u.company_name ?? null,
          journey_status: u.journey_status as UserJourneyStatus,
          journey_last_activity_at: u.journey_last_activity_at,
          current_step_key: current?.step_key ?? null,
          current_step_title: currentStep?.title ?? null,
          current_step_index: currentStep?.step_order ?? null,
          total_steps: totalSteps,
          next_scheduled_for: current?.scheduled_for ?? null,
          has_failed_or_pending_manual: hasFailedOrManual,
        };
      });

      setRows(built);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: progress } = await supabase
        .from('user_journey_progress')
        .select('id, user_id, step_key, updated_at, users(email, company_name), journey_steps(title, type)')
        .eq('status', 'pending_manual')
        .order('updated_at', { ascending: true });

      if (cancelled) return;
      const built: PendingTask[] = (progress ?? []).map((p: any) => ({
        ujp_id: p.id,
        user_id: p.user_id,
        step_key: p.step_key,
        step_title: p.journey_steps?.title ?? p.step_key,
        step_type: p.journey_steps?.type ?? '',
        company_name: p.users?.company_name ?? null,
        email: p.users?.email ?? '',
        updated_at: p.updated_at,
      }));
      setTasks(built);
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    let green = 0, yellow = 0, red = 0, completed = 0;
    rows.forEach(r => {
      if (r.journey_status === 'completed') { completed++; return; }
      const k = computeStoplicht(r.journey_status, r.journey_last_activity_at, r.has_failed_or_pending_manual);
      if (k === 'green') green++;
      else if (k === 'yellow') yellow++;
      else if (k === 'red') red++;
    });
    return { green, yellow, red, completed, total: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    let r = rows;
    if (search.trim()) {
      const s = search.toLowerCase();
      r = r.filter(row => row.email.toLowerCase().includes(s) || (row.company_name ?? '').toLowerCase().includes(s));
    }
    if (filter === 'completed') r = r.filter(row => row.journey_status === 'completed');
    else if (filter !== 'all') {
      r = r.filter(row => {
        if (row.journey_status === 'completed') return false;
        const k = computeStoplicht(row.journey_status, row.journey_last_activity_at, row.has_failed_or_pending_manual);
        return k === filter;
      });
    }
    return r;
  }, [rows, search, filter]);

  const taskCount = tasks?.length ?? 0;
  const tab: 'open' | 'overzicht' = tabOverride ?? (taskCount > 0 ? 'open' : 'overzicht');

  const content = (
    <div>
      {/* Header + tabs in dezelfde stijl als het Campaigns-scherm */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Klanttraject</h1>
            <p className="text-sm text-gray-600 mt-1">Monitor en beheer alle klanttrajecten</p>
          </div>
          <button onClick={() => navigate('/admin/klanttraject/beheer')} className="btn-secondary flex items-center gap-2 hover:scale-105 transition-transform">
            <SettingsIcon className="w-4 h-4" /> Beheer stappen
          </button>
        </div>
      </div>

      <TabNavigation
        tabs={[
          { id: 'open', label: 'Openstaand', icon: Inbox, count: taskCount },
          { id: 'overzicht', label: 'Overzicht', icon: List, count: counts.total },
        ]}
        activeTab={tab}
        onTabChange={(id) => setTabOverride(id as 'open' | 'overzicht')}
      />

      {/* Tab: Openstaand */}
      {tab === 'open' && (
        tasks === null ? (
          <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500 text-sm">Geen openstaande taken, alles loopt.</div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
            {tasks.map(t => (
              <button key={t.ujp_id} onClick={() => navigate(`/admin/users/${t.user_id}?tab=klanttraject`)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{t.company_name || t.email}</div>
                  <div className="text-xs text-slate-500 truncate">{t.email}</div>
                </div>
                <div className="text-xs text-slate-700 hidden sm:block min-w-0 truncate">{t.step_title}</div>
                <div className="text-xs text-slate-500 hidden md:block w-28 text-right">sinds {formatDaysSilence(t.updated_at)}</div>
              </button>
            ))}
          </div>
        )
      )}

      {/* Tab: Overzicht */}
      {tab === 'overzicht' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="Loopt" value={counts.green} active={filter === 'green'} onClick={() => setFilter(filter === 'green' ? 'all' : 'green')} kleur="green" />
            <Tile label="In nudge-fase" value={counts.yellow} active={filter === 'yellow'} onClick={() => setFilter(filter === 'yellow' ? 'all' : 'yellow')} kleur="yellow" />
            <Tile label="Vastgelopen" value={counts.red} active={filter === 'red'} onClick={() => setFilter(filter === 'red' ? 'all' : 'red')} kleur="red" />
            <Tile label="Voltooid" value={counts.completed} active={filter === 'completed'} onClick={() => setFilter(filter === 'completed' ? 'all' : 'completed')} kleur="gray" />
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek op naam of email..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500 text-sm">
              {rows.length === 0 ? 'Nog geen actieve klanttrajecten.' : 'Geen resultaten voor deze filter.'}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
              {filtered.map(row => {
                const k: StoplichtKleur = row.journey_status === 'completed' ? 'gray' : computeStoplicht(row.journey_status, row.journey_last_activity_at, row.has_failed_or_pending_manual);
                return (
                  <button key={row.user_id} onClick={() => navigate(`/admin/users/${row.user_id}?tab=klanttraject`)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${stoplichtBg(k)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{row.company_name || row.email}</div>
                      <div className="text-xs text-slate-500 truncate">{row.email}</div>
                    </div>
                    <div className="text-xs text-slate-600 hidden sm:block min-w-0">
                      {row.current_step_title ? (
                        <>Stap {row.current_step_index}/{row.total_steps}: <span className="text-slate-800">{row.current_step_title}</span></>
                      ) : row.journey_status === 'completed' ? 'Voltooid' : '-'}
                    </div>
                    <div className={`text-xs ${stoplichtTextColor(k)} hidden md:block w-32 text-right`}>
                      {row.journey_status === 'paused' ? 'Gepauzeerd' : row.journey_status === 'completed' ? '-' : row.next_scheduled_for ? formatScheduledFor(row.next_scheduled_for) : formatDaysSilence(row.journey_last_activity_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return <AdminLayout>{content}</AdminLayout>;
}

interface TileProps { label: string; value: number; active: boolean; onClick: () => void; kleur: StoplichtKleur }
function Tile({ label, value, active, onClick, kleur }: TileProps) {
  return (
    <button onClick={onClick} className={`text-left px-4 py-3 rounded-lg border transition ${active ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${stoplichtBg(kleur)}`} />
        <span className="text-xs text-slate-600">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </button>
  );
}
